import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';

import {
  AlertBlock,
  AlertBlockError,
  AlertBlockSuccess,
  IconInformation,
  Timeline,
} from '@klarna/bubble-ui';
import { MOET_CONSTANTS } from 'moet-core';
import { translator } from 'mage-i18n';
import { generateCyElemId } from 'moet-test-utils/cypress/utils';

import { getFirstOrderAt, getMerchant } from '@/selectors/merchant';
import { APP_PATHS } from '@/constants';
// eslint-disable-next-line no-restricted-imports
import { CY_WHATS_NEXT_PAGE_IDS } from '../../constants';

const tPrefix = 'products-fe.pages.whats_next_page.timeline';

const { ONBOARDING_STATUSES_V2, ONBOARDING_STATUS_VALUES_V2 } = MOET_CONSTANTS;

const AlerBlockInfo = props => <AlertBlock {...props} iconType={IconInformation} />;

const getSummaryPath = merchantStatus => {
  // we need an extra suspended check because its value is -1
  if (
    ONBOARDING_STATUS_VALUES_V2[merchantStatus] ===
      ONBOARDING_STATUS_VALUES_V2[ONBOARDING_STATUSES_V2.SUSPENDED] ||
    ONBOARDING_STATUS_VALUES_V2[merchantStatus] ===
      ONBOARDING_STATUS_VALUES_V2[ONBOARDING_STATUSES_V2.TERMINATED]
  ) {
    return APP_PATHS.APPLICATION_OVERVIEW_PATH;
  }
  if (
    ONBOARDING_STATUS_VALUES_V2[merchantStatus] <
    ONBOARDING_STATUS_VALUES_V2[ONBOARDING_STATUSES_V2.TRANSACTIONS_REVIEW_AUTOMATED]
  ) {
    return APP_PATHS.APPLICATION_CONFIRMATION_PATH;
  }

  return APP_PATHS.APPLICATION_OVERVIEW_PATH;
};

const ApplicationSummaryAlert = ({ basePath, history, merchant, t }) => {
  const Alert =
    {
      [ONBOARDING_STATUSES_V2.SUSPENDED]: AlertBlockError,
      [ONBOARDING_STATUSES_V2.TERMINATED]: AlertBlockError,
      [ONBOARDING_STATUSES_V2.TRANSACTIONS_DISABLED]: AlertBlockError,
      [ONBOARDING_STATUSES_V2.COMPLETED]: AlertBlockSuccess,
    }[merchant.status] || AlerBlockInfo;

  const getContent = () => {
    if (
      merchant.status === ONBOARDING_STATUSES_V2.SUSPENDED ||
      merchant.status === ONBOARDING_STATUSES_V2.TERMINATED
    ) {
      return t(`${tPrefix}.application.alert.rejected`);
    } else if (merchant.status === ONBOARDING_STATUSES_V2.TRANSACTIONS_DISABLED) {
      return t(`${tPrefix}.application.alert.review`);
    }
  };

  return (
    <Alert
      content={[getContent()]}
      linkLabel={t(`${tPrefix}.application.alert.view_summary`)}
      onClick={() =>
        history.push(`${basePath}/${getSummaryPath(merchant.status)}${history.location.search}`)
      }
      {...generateCyElemId(CY_WHATS_NEXT_PAGE_IDS.BTN_OPEN_APPLICATION_CONFIRMATION, window)}
    />
  );
};

const WhatsNextTimeline = ({ basePath, firstOrderAt, history, merchant, t }) => {
  const [entries, setEntries] = useState([]);

  useEffect(() => {
    const timelineEntries = [
      {
        title: t(`${tPrefix}.application.title`),
        description: new Date(
          // some MIDs that are not onboarded by us end up on products app and they won't have
          // transactionsRequestedAt instead of failing, just show creation date
          merchant.timestamps.transactionsRequestedAt || merchant.timestamps.createdAt,
        ).toLocaleDateString(),
        customContent: (
          <ApplicationSummaryAlert
            basePath={basePath}
            history={history}
            merchant={merchant}
            t={t}
          />
        ),
      },
    ];

    if (
      merchant.timestamps.payoutsApprovedAt &&
      merchant.status !== ONBOARDING_STATUSES_V2.SUSPENDED &&
      merchant.status !== ONBOARDING_STATUSES_V2.TERMINATED
    ) {
      timelineEntries.unshift({
        title: t(`${tPrefix}.payouts.title`),
        description: new Date(merchant.timestamps.payoutsApprovedAt).toLocaleDateString(),
      });
    }

    if (
      ONBOARDING_STATUS_VALUES_V2[merchant.status] >=
      ONBOARDING_STATUS_VALUES_V2.TRANSACTIONS_ENABLED
    ) {
      if (firstOrderAt) {
        timelineEntries.unshift({
          title: t(`${tPrefix}.integration_complete.title`),
          description: new Date(firstOrderAt).toLocaleDateString(),
        });
      } else {
        timelineEntries.unshift({
          title: t(`${tPrefix}.integration.title`),
          description: new Date().toLocaleDateString(),
          customContent: <AlerBlockInfo content={[t(`${tPrefix}.integration.alert`)]} />,
        });
      }
    }

    setEntries(timelineEntries);
  }, [merchant]);

  return <Timeline entries={entries} />;
};

WhatsNextTimeline.propTypes = ApplicationSummaryAlert.propTypes = {
  basePath: PropTypes.string.isRequired,
  history: PropTypes.object.isRequired,
  merchant: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  firstOrderAt: PropTypes.string,
};

export default compose(
  connect(state => ({
    firstOrderAt: getFirstOrderAt(state),
    merchant: getMerchant(state),
    t: translator(state),
  })),
  withRouter,
)(WhatsNextTimeline);
